import { useState, useEffect } from 'react'
import i from 'i18next'
import slugify from 'slugify'
import Head from 'next/head'
import Error from 'next/error'
import { useRouter } from 'next/router'
import { motion } from 'framer-motion'
import useTranslations, { getTranslations } from 'lib/hooks/use-translations'
import {
	getProduct,
	ProductDetail,
	ProductThumbnail,
	getProducts,
	getProductRecommendations,
} from 'lib/hooks/use-products'
import { getIngredients } from 'lib/hooks/use-ingredients'
import { getFAQs } from 'lib/hooks/use-faqs'
import {
	getMenuCollections,
	MenuCollection,
	getCollectionProducts,
	getMenu,
	MenuLink,
} from 'lib/hooks/use-collections'
import Layout from 'components/Layout'
import Footer from 'components/sections/Footer'
import TextImageHalf from 'components/sections/TextImageHalf'
import Products from 'components/sections/Products'
import { Questions } from 'components/sections/FAQ'
import Loading from 'components/product/Loading'
import Detail from 'components/product/Detail'
import { getRating, RatingWithStars } from 'lib/hooks/use-reviews'
import Reviews from 'components/sections/Reviews'
import Json from 'components/product/Json'
import Nav from 'components/product/Nav'
import { IngredientsCTA } from 'components/flyouts/Ingredients'
import { res } from 'components/Image'
import Collapsible from 'components/sections/Collapsible'
import { PackagingCTA } from 'components/flyouts/Packaging'
import { getPackaging } from 'lib/hooks/use-packaging'
import { gtmPush } from 'components/analytics/GTM'
import { cvgTrack, parseProduct } from 'components/analytics/Converge'
import useStorefront from 'lib/hooks/use-storefront'

export async function getStaticPaths() {
	const paths = []
	const collections = await getMenuCollections( false )

	for ( const { handle } of collections ) {
		const collection = await getCollectionProducts({ handle })

		// Do a ? check because the collection might be unavailable
		collection?.products.forEach( product => {
			paths.push({ params: {
				collection: collection.handle,
				product: [ product.handle ],
			} })
		})
	}

	return { paths, fallback: true }
}

export async function getStaticProps({ params: { collection, product: handles } }) {
	const handle = handles[ 0 ]

	// Make sure there is no product page for the free gift products
	if ( /free-/.test( handle ) ) return { notFound: true }

	const translations = await getTranslations({ page: 'product' })
	const nav = await getMenu()
	const collections = await getMenuCollections()
	const product = await getProduct({ handle })

	if ( !product ) {
		// Query redirects
		const client = useStorefront()
		const { urlRedirects } = await client.request( `{
			urlRedirects(first: 1, query:"path:\\"/products/${handle}\\"") {
				edges {
					node {
						path
						target
					}
				}
			}
		}` )

		if ( urlRedirects.edges.length ) {
			const { target } = urlRedirects.edges[ 0 ].node
			return {
				redirect: {
					destination: target.replace( '/products/', `/products/${collection}/` ),
					permanent: false,
				},
			}
		}

		return { notFound: true }
	}

	const recommendations = await getProductRecommendations( product.id )
	const ingredients = await getIngredients( product )
	const packaging = await getPackaging( product )
	const packs = await getProducts( `${product?.handle}-packs`, 3 )
	const faqs = product ? await getFAQs( product ) : []
	const reviews = product ? await getRating({
		handle: product.handle,
		tags: product.tags,
		stars: true,
	}) : null

	return {
		// eslint-disable-next-line max-len
		props: { handles, translations, nav, collections, product, ingredients, packaging, reviews, packs, faqs, recommendations },
		revalidate: 60 * 30,
	}
}

export const findVariant = (
	variants: ProductDetail['variants'],
	options: any,
	onNotFound: () => void
) => (
	variants.find( variant => {
		let match = true

		Object.entries( options ).map( ( [ name, value ] ) => {
			const selection = o => (
				name === o.name &&
				value === slugify( o.value, { lower: true })
			)
			if ( !variant.selectedOptions.find( selection ) ) match = false
		})

		return match
	}) || ( () => {
		// Not all option combinations have a variant; should happen often, but
		// handle this to prevent page breaking errors
		onNotFound()
		return variants[ 0 ]
	})()
)

type Props = {
	loaded: boolean,
	translations: any
	nav: MenuLink[]
	collections: MenuCollection[]
	handles: string[]
	product: ProductDetail
	ingredients: Array<{ ingredient: string, purpose: string }>
	packaging: Array<{ element: string, material: string, recycling: string }>
	reviews: RatingWithStars
	packs: ProductThumbnail[]
	faqs: any[]
	recommendations: ProductThumbnail[]
}

export default function Product({
	loaded,
	translations,
	nav,
	collections,
	handles,
	product,
	ingredients,
	packaging,
	reviews,
	packs,
	faqs,
	recommendations,
}: Props ) {
	useTranslations( translations )
	const { isFallback, query } = useRouter()
	const [ options, setOptions ] = useState({})

	// Use optional third part of the URL as perfume selection
	useEffect( () => {
		if ( handles?.length > 1 ) setOptions({ Perfume: handles[ 1 ] })
	}, [ handles ] )

	// Redirect to /products on 404
	// useEffect( () => {
	// 	if ( !isFallback && !product ) replace( '/products' )
	// }, [ isFallback, !product ] )

	if ( isFallback ) return <Loading />
	if ( !isFallback && !product ) return (
		<Error statusCode={404} title="This product could not be found" />
	)

	// Find variant based on the current selection of options
	const variant = findVariant( product.variants, options, () => setOptions({}) )

	const variantOptions = Object.fromEntries(
		variant.selectedOptions.map( o => ( [
			o.name,
			slugify( o.value, { lower: true }),
		] ) )
	)

	let { collection } = query
	if ( collection === 'all' ) {
		const match = product.collections?.find( c =>
			collections.findIndex( ({ handle }) => handle === c ) > -1
		)

		if ( match ) collection = match
	}

	const url = `https://ray.care/products/${collection}/${product.handle}`
	const description = i.t( [
		`product.description.${product.handle}`,
		product.description,
	] )
	const variantId = variant.id.match( /[^/]+$/ )?.[ 0 ]

	return (
		<>
			<Track product={product} variant={variant} query={query} />
			<Layout loaded={loaded} nav={nav} collections={collections}>
				<Head>
					<title>
						{i.t(
							[ `title.product.${product.handle}`, 'title.product' ],
							{
								product: product.title,
								suffix: i.t( 'title.suffix' ),
							}
						)}
					</title>
					{/* eslint-disable max-len */}
					<meta name="description" content={description} />
					<meta property="variant:id" content={variantId} />
					<meta property="og:url" content={url} />
					<meta property="og:title" content={i.t( 'product.title', { product: product.title })} />
					<meta property="og:type" content="product" />
					<meta property="og:description" content={description} />
					<meta property="og:price:amount" content={variant.priceV2.amount} />
					<meta property="og:price:currency" content="EUR" />
					<meta property="og:image" content={res( variant.image.originalSrc, '1200x628_pad_f7f7f7' )} />
					<meta property="og:image:width" content="1200" />
					<meta property="og:image:height" content="628" />
					<meta name="twitter:card" content="summary_large_image" />
					<meta name="twitter:title" content={i.t( 'product.title', { product: product.title })} />
					<meta name="twitter:description" content={description} />

					<link rel="canonical" href={url} />
					{/* eslint-enable max-len */}
				</Head>
				<Nav
					product={product}
					variant={variant}
					packs={!!packs?.length}
					faqs={!!faqs.length}
					reviews={!!reviews.num}
				/>
				<Detail
					key={product.handle}
					product={product}
					variant={variant}
					reviews={reviews.num}
					rating={reviews.average}
					options={{ ...variantOptions, ...options }}
					setOptions={setOptions}
				/>
				<motion.div
					id="details"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ delay: 1 }}
				>
					<Collapsible
						id="benefits"
						title={i.t( 'product.nav.benefits' )}
						content={i.t(
							product.metafields[ 'benefits-nl' ] ||
							'product.benefits.generic',
							{ postProcess: 'markdown-jsx' }
						)}
						collapsed
					/>
					<Collapsible
						id="ingredients"
						title={i.t( 'product.nav.ingredients' )}
						content={(
							<>
								{i.t(
									product.metafields[ 'ingredients-nl' ] ||
									'product.ingredients.generic',
									{ postProcess: 'markdown-jsx' }
								)}
								{ingredients.length > 0 && (
									<IngredientsCTA
										product={i.t( 'product.title', { product: product.title })}
										ingredients={ingredients}
									/>
								)}
							</>
						)}
						collapsed
					/>
					<Collapsible
						id="packaging"
						title={i.t( 'product.nav.packaging' )}
						content={(
							<>
								{i.t(
									product.metafields[ 'packaging-nl' ] ||
									'product.packaging.generic',
									{ postProcess: 'markdown-jsx' }
								)}
								{packaging.length > 0 && (
									<PackagingCTA
										product={i.t( 'product.title', { product: product.title })}
										packaging={packaging}
									/>
								)}
							</>
						)}
						collapsed
					/>
					{faqs.length > 0 && (
						<Collapsible
							id="faq"
							title={i.t( 'product.nav.faq' )}
							content={(
								<>
									<h2>
										{i.t( 'product.faq.title', {
											product: product.title.toLowerCase(),
										})}
									</h2>
									<Questions questions={faqs} />
								</>
							)}
							collapsed
						/>
					)}
					{reviews.num > 0 && (
						<Collapsible
							id="reviews"
							title={i.t( 'product.nav.reviews' )}
							content={(
								<Reviews
									handle={product.handle}
									tags={product.tags}
									{...reviews}
								/>
							)}
						/>
					)}
					{packs?.length > 0 && (
						<Collapsible
							id="packs"
							title={i.t( 'product.nav.packs' )}
							content={<Products products={packs} />}
						/>
					)}
					<span id="end" />
					<Products
						title={i.t( [
							`product.others.${product.handle}`,
							'product.others.generic',
						] )}
						products={recommendations}
					/>
					<TextImageHalf
						align="right"
						text={i.t( 'product.story', { postProcess: 'markdown-jsx' })}
						image={i.t( 'product.story.image' )}
					/>
				</motion.div>
				<Footer />
				<Json product={product} rating={reviews} />
				<style jsx>{`
					span {
						position: relative;
						top: 20px;
					}

					:global( div.shipping:hover ) {
						background-color: white;
					}
				`}</style>
			</Layout>
		</>
	)
}

const Track = ({ product, variant, query }) => {
	// Track product view in Klaviyo
	useEffect( () => {
		window._learnq = window._learnq || []
		window._learnq.push( [ 'track', 'Viewed Product', {
			'ProductName': product.title,
			'ProductID': product.id,
			'Categories': [ query.collection ],
			'ImageURL': variant.image.originalSrc,
			'URL': window.location.toString(),
			'Brand': 'Ray',
			'Price': variant.priceV2.amount,
		}] )

		cvgTrack({
			eventName: 'Viewed Product',
			properties: parseProduct( product, variant ),
		})

		gtmPush({
			event: 'viewItem',
			ecommerce: {
				detail: {
					products: [{
						name: product.title,
						id: product.id.substring( product.id.lastIndexOf( '/' ) + 1 ),
						price: variant.priceV2.amount,
						brand: 'Ray',
						category: query.collection,
						variant: variant.selectedOptions.map( o => o.value ).join( ' / ' ),
					}],
				},
			},
		})
	}, [ product.id ] )

	return null
}
